// Pins numeric columns to their exact measured-content width, so they never truncate —
// ports the live pdfExporter.js's measureRequiredNumericColumnWidths logic verbatim (it has
// no DevExtreme dependency: it only ever used doc.getTextDimensions + formatNumber + plain
// array reduces). Text columns are left for autotable's own native 'auto' width mode, which
// exists specifically because autotable (unlike DevExtreme's exporter) has a real column-
// width API — the elaborate shrink/grow/fontScale-lockstep system in the old engine existed
// only to route around DevExtreme not having one.
import { formatNumber } from 'devextreme/localization';

const CONTENT_WIDTH_PADDING_PT = 8;

// Header, groupFooter, and grandTotal rows all render bold at fontSize + headerGroupFontSizeBoost
// (see pdfHooksV2.js's didParseCell) — a couple points larger than the plain body font used for
// ordinary data rows. This factor is a cheap stand-in for measuring actual bold-glyph width
// (getTextDimensions measures at whatever font style happens to be active on the doc at call
// time, not the bold style these cells render with) so caption/footer-sum floors aren't
// underestimated relative to what's actually drawn.
const BOLD_WIDTH_FUDGE_FACTOR = 1.08;

export function measureRequiredNumericWidths(doc, leafColumns, rows, groupByFields, fontSize, headerFontSize) {
    const widths = {};
    const captionFontSize = headerFontSize || fontSize;

    leafColumns.forEach((col) => {
        if (col.dataType !== 'number') return;

        // Caption is rendered as the bold, boosted header — measure at that size/weight, not
        // the plain body fontSize, or the column can end up narrower than what's actually drawn.
        let maxWidth = doc.getTextDimensions(String(col.caption || ''), { fontSize: captionFontSize }).w * BOLD_WIDTH_FUDGE_FACTOR;

        const formatForMeasurement = (value) => (col.format ? formatNumber(value, col.format) : String(value));

        rows.forEach((row) => {
            const value = row[col.dataField];
            if (typeof value !== 'number') return;
            const w = doc.getTextDimensions(formatForMeasurement(value), { fontSize }).w;
            if (w > maxWidth) maxWidth = w;
        });

        // Group-subtotal upper bound: the widest single group's sum-of-abs, mirroring the
        // live pdfExporter.js's maxGroupSum logic (Topic 4 item 8) — a group subtotal can have
        // far more digits than any individual row's value. Rendered bold+boosted in the
        // groupFooter row, same as the caption, so measured the same way.
        if (groupByFields && groupByFields.length) {
            const bucketSums = new Map();
            rows.forEach((row) => {
                const v = row[col.dataField];
                if (typeof v !== 'number') return;
                const key = groupByFields.map((f) => row[f]).join('|');
                bucketSums.set(key, (bucketSums.get(key) || 0) + Math.abs(v));
            });
            let maxGroupSum = 0;
            bucketSums.forEach((sum) => { if (sum > maxGroupSum) maxGroupSum = sum; });
            if (maxGroupSum > 0) {
                const w = doc.getTextDimensions(formatForMeasurement(maxGroupSum), { fontSize: captionFontSize }).w * BOLD_WIDTH_FUDGE_FACTOR;
                if (w > maxWidth) maxWidth = w;
            }
        } else {
            const sumOfAbs = rows.reduce((acc, row) => {
                const v = row[col.dataField];
                return typeof v === 'number' ? acc + Math.abs(v) : acc;
            }, 0);
            if (sumOfAbs > 0) {
                const w = doc.getTextDimensions(formatForMeasurement(sumOfAbs), { fontSize: captionFontSize }).w * BOLD_WIDTH_FUDGE_FACTOR;
                if (w > maxWidth) maxWidth = w;
            }
        }

        widths[col.dataField] = maxWidth + CONTENT_WIDTH_PADDING_PT;
    });

    return widths;
}

// Floor for non-numeric columns: the widest SINGLE WORD in the header caption, not the whole
// caption. Autotable will happily wrap a multi-word header onto several lines, but if a column
// is squeezed narrower than its longest word, autotable's own resize fallback (calculateWidths
// in jspdf-autotable, second pass) ignores word-readability entirely and jsPDF hard-breaks that
// word letter-by-letter — this is what produced the vertical single-letter-per-line headers.
// Giving every column this floor (via columnStyles minCellWidth) keeps autotable's first,
// word-safe resize pass sufficient so that fallback never needs to run.
function measureMinReadableHeaderWidth(doc, col, headerFontSize) {
    const caption = String(col.caption || '');
    const words = caption.split(/\s+/).filter(Boolean);
    let maxWordWidth = 0;
    words.forEach((word) => {
        const w = doc.getTextDimensions(word, { fontSize: headerFontSize }).w;
        if (w > maxWordWidth) maxWordWidth = w;
    });
    return maxWordWidth * BOLD_WIDTH_FUDGE_FACTOR + CONTENT_WIDTH_PADDING_PT;
}

// headerFontSize, not the plain body fontSize — headers always render at fontSize +
// headerGroupFontSizeBoost (bold), so that's the size this floor must actually protect against.
export function measureHeaderMinReadableWidths(doc, leafColumns, headerFontSize) {
    const widths = {};
    leafColumns.forEach((col) => {
        widths[col.dataField] = measureMinReadableHeaderWidth(doc, col, headerFontSize);
    });
    return widths;
}

// Mirrors the live pdfExporter.js's fontScale policy (Topic 2 of the original session): shrink
// the font size to make the table's column-width floors fit the page, preferring not to drop
// below `preferredMinFontSize` (logs once if it must) but never stopping there at the cost of
// page-fit — only a near-zero ABSOLUTE floor is a hard stop, purely to avoid degenerate
// zero/negative widths.
const ABSOLUTE_MIN_FONT_SIZE_PT = 3;
const FONT_SIZE_STEP_PT = 0.5;

export function resolveFitToPageLayout({
    doc,
    leafColumns,
    rows,
    groupByFields,
    availableWidth,
    baseFontSize,
    headerFontSizeBoost,
    preferredMinFontSize,
}) {
    let fontSize = baseFontSize;
    let numericWidths = measureRequiredNumericWidths(doc, leafColumns, rows, groupByFields, fontSize, fontSize + headerFontSizeBoost);
    let headerMinWidths = measureHeaderMinReadableWidths(doc, leafColumns, fontSize + headerFontSizeBoost);
    let warnedBelowPreferred = false;

    const requiredTotal = () => leafColumns.reduce((sum, col) => {
        const floor = col.dataType === 'number' ? numericWidths[col.dataField] : headerMinWidths[col.dataField];
        return sum + (floor || 0);
    }, 0);

    while (requiredTotal() > availableWidth && fontSize > ABSOLUTE_MIN_FONT_SIZE_PT) {
        fontSize = Math.max(ABSOLUTE_MIN_FONT_SIZE_PT, fontSize - FONT_SIZE_STEP_PT);
        if (!warnedBelowPreferred && fontSize < preferredMinFontSize) {
            warnedBelowPreferred = true;
            // eslint-disable-next-line no-console
            console.log('[pdf-export-v2 DEBUG] font size dropped below the preferred readability floor to guarantee page fit:', preferredMinFontSize, '→', fontSize);
        }
        numericWidths = measureRequiredNumericWidths(doc, leafColumns, rows, groupByFields, fontSize, fontSize + headerFontSizeBoost);
        headerMinWidths = measureHeaderMinReadableWidths(doc, leafColumns, fontSize + headerFontSizeBoost);
    }

    return { fontSize, numericWidths, headerMinWidths };
}
